import React, { useState, useEffect } from 'react';
import Cookies from 'universal-cookie';
import {
  Building2,
  Plus,
  Edit3,
  Search,
  Phone,
  Mail,
  MapPin,
  FileText,
  Layers,
  ShieldCheck,
  Paperclip,
  Warehouse as WarehouseIcon
} from 'lucide-react';
import { Society } from '../../../../types/Society.types';
import { Warehouse } from '../../../../types/Warehouse.types';
import { CostCenter } from '../../../../types/CostCenter.types';
import { MOCK_WAREHOUSES } from '../../../../mocks/Warehouses.mocks';
import { ModuleHeader } from '../../../../components/shared/ModuleHeader/ModuleHeader';
import { useToast } from '../../../../hooks/useToast';
import Modal from '../../../../components/shared/Modal/Modal';
import ObexRequestsHandlerP80 from '../../../../handlers/ObexRequestsHandlerP80';
import { listCostCenters, createCostCenter } from '../../../../services/CostCentersService';
import { mapCompany } from '../../../../services/CompaniesService';
import store from '../../../../redux/store';
import { companiesLoaded } from '../../../../redux/slices/activeCompanySlice';
import { useEntityAccess } from '../../../../hooks/useEntityAccess';
import {
  AeatEnvironment,
  parseAeatCertificate,
  keyMatchesCertificate,
  saveAeatCredentials
} from '../../../../services/CompanyAeatCredentialsService';
import { Skeleton } from '../../../../components/shared/Skeleton/Skeleton';
import { DataTable, ColumnDef } from '../../../../components/shared/DataTable/DataTable';
import { MasterDetailLayout } from '../../../../components/shared/MasterDetail/MasterDetailLayout';
import { RowActionsMenu, viewAction, editAction, deleteAction } from '../../../../components/shared/RowActionsMenu/RowActionsMenu';
import { DetailPanel } from '../../../../components/shared/DetailPanel/DetailPanel';
import {
  DetailField,
  DetailFieldList,
  DetailHeaderButton,
  DetailIdentity,
  DetailSection
} from '../../../../components/shared/DetailPanel/DetailBlocks';
import { SidebarForm } from '../../../../components/shared/SidebarForm/SidebarForm';
import { useClickOutside, isDetailKeepZone } from '../../../../hooks/useClickOutside';
import './SocietiesView.css';

export const SocietiesView = () => {
  const { notify } = useToast();
  const [societies, setSocieties] = useState<Society[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // List, create and update are wired to real APIs. Delete has no backend endpoint
  // yet, so that control stays disabled instead of faking a local-only mutation.
  const [listUnavailable, setListUnavailable] = useState(false);
  const [warehouses] = useState<Warehouse[]>(MOCK_WAREHOUSES);
  const [selectedSociety, setSelectedSociety] = useState<Society | null>(null);
  const [deleteModalSociety, setDeleteModalSociety] = useState<{ id: string; name: string } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Alta y edición de sociedad comparten formulario: editingId vacío es alta.
  // companies-delete no existe todavía, así que la baja sigue deshabilitada.
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingId, setEditingId] = useState('');
  const [newSocietyForm, setNewSocietyForm] = useState({ name: '', cif: '', address: '', phone: '', email: '' });
  const [createError, setCreateError] = useState('');
  const [isSavingSociety, setIsSavingSociety] = useState(false);

  // Centros de coste: son por sociedad y el maestro es demasiado pequeño para
  // justificar vista propia, así que van en un modal sobre la ficha.
  const [isCostCentersOpen, setIsCostCentersOpen] = useState(false);
  const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
  const [costCentersLoading, setCostCentersLoading] = useState(false);
  const [costCentersError, setCostCentersError] = useState('');
  const [newCostCenterCode, setNewCostCenterCode] = useState('');
  const [newCostCenterName, setNewCostCenterName] = useState('');
  const [isSavingCostCenter, setIsSavingCostCenter] = useState(false);

  const getAccessToken = () => {
    const cookies = new Cookies();
    const userCookie = cookies.get('101Obex');
    return userCookie?.token || '';
  };

  const fetchSocieties = async () => {
    const token = getAccessToken();
    if (!token) { setIsLoading(false); setListUnavailable(true); return; }
    setIsLoading(true);
    try {
      let res = await ObexRequestsHandlerP80.get(`/companies-list?access_token=${encodeURIComponent(token)}&page=1&page_size=100`);
      if (res.data) res = res.data;
      if (res.result) res = res.result;
      const list = res.response?.data || res.response || res.data || res;
      if (Array.isArray(list)) {
        const mapped: Society[] = list.map(mapCompany);
        setSocieties(mapped);
        setListUnavailable(false);
        setSelectedSociety(prev => (prev ? mapped.find(s => s.id === prev.id) || null : null));
      } else {
        setSocieties([]);
        setListUnavailable(true);
      }
    } catch (e) {
      console.error('Error fetching companies:', e);
      setSocieties([]);
      setListUnavailable(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSocieties();
  }, []);

  const handleCreateNewClick = () => {
    setEditingId('');
    setSelectedSociety(null);
    setNewSocietyForm({ name: '', cif: '', address: '', phone: '', email: '' });
    setCreateError('');
    setIsCreateOpen(true);
  };

  const handleSelectSociety = (society: Society) => {
    setIsCreateOpen(false);
    setEditingId('');
    setSelectedSociety(society);
  };

  const closePanel = () => {
    setIsCreateOpen(false);
    setEditingId('');
    setSelectedSociety(null);
  };

  // Clic fuera cierra la ficha. El alta no: esta vista no avisa de cambios
  // sin guardar y se perdería lo escrito; se cierra con la X.
  useClickOutside(isDetailKeepZone, closePanel, Boolean(selectedSociety) && !isCreateOpen);

  const CIF_REGEX = /^[A-HJ-NP-SU-W][0-9]{8}$/i;

  const handleSaveNewSociety = async () => {
    const { name, cif, address, phone, email } = newSocietyForm;
    if (!name.trim() || !cif.trim()) {
      setCreateError('La razón social y el CIF son obligatorios.');
      return;
    }
    if (!CIF_REGEX.test(cif.trim())) {
      setCreateError('El formato de CIF no es válido (Ej: A82345678).');
      return;
    }
    const token = getAccessToken();
    if (!token) {
      setCreateError('Sesión no válida.');
      return;
    }
    setIsSavingSociety(true);
    setCreateError('');
    try {
      if (editingId) {
        const url = `/companies-update?access_token=${encodeURIComponent(token)}&company_id=${encodeURIComponent(editingId)}` +
          `&name=${encodeURIComponent(name.trim())}&cif=${encodeURIComponent(cif.trim().toUpperCase())}` +
          `&address=${encodeURIComponent(address.trim())}&phone=${encodeURIComponent(phone.trim())}&email=${encodeURIComponent(email.trim())}`;
        let res = await ObexRequestsHandlerP80.get(url);
        if (res.success === false && res.message) throw new Error(res.message);
        if (res.data) res = res.data;
        if (res.result) res = res.result;
        const saved = res.response;
        if (!saved || typeof saved !== 'object' || Array.isArray(saved) || !saved.id) {
          throw new Error(typeof saved === 'string' ? saved : 'El backend no confirmó la modificación de la sociedad.');
        }
        const previous = societies.find(x => x.id === editingId);
        const updated: Society = {
          ...(previous || {}),
          id: String(saved.id),
          name: saved.name || name.trim(),
          cif: saved.cif || cif.trim(),
          address: saved.address || '',
          phone: saved.phone || '',
          email: saved.email || ''
        };
        setSocieties(prev => prev.map(x => (x.id === updated.id ? updated : x)));
        // El selector de sociedad de la cabecera lee de Redux
        const { companies } = store.getState().activeCompany;
        store.dispatch(companiesLoaded({ companies: companies.map(c => (c.id === updated.id ? { ...c, ...updated } : c)) }));
        setSelectedSociety(updated);
        setEditingId('');
        setIsCreateOpen(false);
        notify('Éxito', 'Sociedad actualizada correctamente', 'info');
        return;
      }

      let url = `/companies-create?access_token=${encodeURIComponent(token)}&name=${encodeURIComponent(name.trim())}&cif=${encodeURIComponent(cif.trim().toUpperCase())}`;
      if (address.trim()) url += `&address=${encodeURIComponent(address.trim())}`;
      if (phone.trim()) url += `&phone=${encodeURIComponent(phone.trim())}`;
      if (email.trim()) url += `&email=${encodeURIComponent(email.trim())}`;

      let res = await ObexRequestsHandlerP80.get(url);
      if (res.data) res = res.data;
      if (res.result) res = res.result;
      const created = res.response;
      if (!created || typeof created !== 'object' || Array.isArray(created) || !created.id) {
        throw new Error(typeof created === 'string' ? created : 'El backend no confirmó la creación de la sociedad.');
      }

      const mapped: Society = {
        id: String(created.id),
        name: created.name || name,
        cif: created.cif || cif,
        address: created.address || '',
        phone: created.phone || '',
        email: created.email || ''
      };
      setSocieties(prev => [mapped, ...prev]);
      setSelectedSociety(mapped);
      setIsCreateOpen(false);
      notify('Éxito', 'Sociedad creada correctamente', 'info');
    } catch (e) {
      console.error('Error creating society:', e);
      setCreateError(e instanceof Error ? e.message : `Error al ${editingId ? 'modificar' : 'crear'} la sociedad.`);
    } finally {
      setIsSavingSociety(false);
    }
  };

  // Certificado AEAT (DIVA) de la sociedad seleccionada. 'view' solo consulta; 'full' sube y reemplaza.
  const { level: aeatAccess } = useEntityAccess('certificados_sociedad');
  const EMPTY_AEAT_FORM = { environment: 'test' as AeatEnvironment, ilrCode: '', passphrase: '' };
  const [aeatForm, setAeatForm] = useState(EMPTY_AEAT_FORM);
  const [aeatCertFile, setAeatCertFile] = useState<{ name: string; text: string } | null>(null);
  const [aeatKeyFile, setAeatKeyFile] = useState<{ name: string; text: string } | null>(null);
  const [aeatError, setAeatError] = useState('');
  const [isSavingAeat, setIsSavingAeat] = useState(false);

  useEffect(() => {
    setAeatForm(EMPTY_AEAT_FORM);
    setAeatCertFile(null);
    setAeatKeyFile(null);
    setAeatError('');
  }, [selectedSociety?.id]);

  const readTextFile = (file: File | undefined, setter: (f: { name: string; text: string } | null) => void) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setter({ name: file.name, text: String(reader.result || '') });
    reader.readAsText(file);
  };

  const handleSaveAeat = async () => {
    if (!selectedSociety || aeatAccess !== 'full') return;
    if (!aeatForm.ilrCode.trim() || !aeatCertFile || !aeatKeyFile) {
      setAeatError('Indica el código ILR y los ficheros de certificado y clave.');
      return;
    }
    setIsSavingAeat(true);
    setAeatError('');
    try {
      const info = parseAeatCertificate(aeatCertFile.text);
      if (info.validTo.getTime() < Date.now()) throw new Error('El certificado está caducado.');
      if (!keyMatchesCertificate(aeatCertFile.text, aeatKeyFile.text, aeatForm.passphrase)) {
        notify('Aviso', 'No se pudo comprobar que la clave corresponda al certificado. Se guarda igualmente.', 'warning');
      }
      const summary = await saveAeatCredentials({
        companyId: selectedSociety.id,
        environment: aeatForm.environment,
        ilrCode: aeatForm.ilrCode.trim(),
        certificatePem: aeatCertFile.text,
        privateKeyPem: aeatKeyFile.text,
        passphrase: aeatForm.passphrase,
        info
      });
      const updated: Society = { ...selectedSociety, aeat: { ...selectedSociety.aeat, [summary.environment]: summary } };
      setSocieties(prev => prev.map(s => (s.id === updated.id ? updated : s)));
      setSelectedSociety(updated);
      setAeatCertFile(null);
      setAeatKeyFile(null);
      setAeatForm(f => ({ ...f, passphrase: '' }));
      notify('Éxito', 'Certificado AEAT guardado correctamente', 'info');
    } catch (e) {
      console.error('Error saving AEAT credentials:', e);
      setAeatError(e instanceof Error ? e.message : 'Error al guardar el certificado.');
    } finally {
      setIsSavingAeat(false);
    }
  };

  const handleEditClick = (society: Society) => {
    setSelectedSociety(society);
    setNewSocietyForm({ name: society.name, cif: society.cif, address: society.address, phone: society.phone, email: society.email });
    setEditingId(society.id);
    setCreateError('');
    setIsCreateOpen(true);
  };

  const handleDelete = (_societyId: string, _societyName: string) => {
    notify('No disponible', 'La eliminación de sociedades todavía no está conectada a ningún API del backend.', 'warning');
  };

  const confirmDeleteSociety = () => {
    if (!deleteModalSociety) return;
    const { id } = deleteModalSociety;
    setSocieties(prev => prev.filter(s => s.id !== id));
    if (selectedSociety?.id === id) {
      setSelectedSociety(null);
    }
    setDeleteModalSociety(null);
    notify('Éxito', 'Sociedad eliminada exitosamente', 'info');
  };

  const openCostCenters = async (society: Society) => {
    setIsCostCentersOpen(true);
    setCostCentersLoading(true);
    setCostCentersError('');
    setNewCostCenterCode('');
    setNewCostCenterName('');
    try {
      setCostCenters(await listCostCenters({ companyId: society.id }));
    } catch (e) {
      console.error('Error fetching cost centers:', e);
      setCostCenters([]);
      setCostCentersError(e instanceof Error ? e.message : 'No se pudieron cargar los centros de coste.');
    } finally {
      setCostCentersLoading(false);
    }
  };

  const handleAddCostCenter = async () => {
    if (!selectedSociety) return;
    if (!newCostCenterCode.trim() || !newCostCenterName.trim()) {
      setCostCentersError('El código y el nombre son obligatorios.');
      return;
    }
    setIsSavingCostCenter(true);
    setCostCentersError('');
    try {
      const created = await createCostCenter({
        companyId: selectedSociety.id,
        code: newCostCenterCode.trim().toUpperCase(),
        name: newCostCenterName.trim()
      });
      setCostCenters(prev => [...prev, created]);
      setNewCostCenterCode('');
      setNewCostCenterName('');
      notify('Éxito', 'Centro de coste creado exitosamente', 'info');
    } catch (e) {
      console.error('Error creating cost center:', e);
      setCostCentersError(e instanceof Error ? e.message : 'Error al crear el centro de coste.');
    } finally {
      setIsSavingCostCenter(false);
    }
  };

  const filteredSocieties = societies.filter(s =>
    s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.cif.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const linkedWarehouses = (societyId: string) => warehouses.filter(w => w.societyId === societyId);

  const columns: ColumnDef<Society>[] = [
    {
      id: 'id',
      header: 'SOC_ID',
      render: (society) => <span className="societies-view__soc-id">{society.id}</span>
    },
    {
      id: 'name',
      header: 'NOMBRE / CIF',
      render: (society) => (
        <>
          <div className="societies-view__soc-name">{society.name}</div>
          <div className="societies-view__soc-cif">CIF: {society.cif}</div>
        </>
      )
    },
    {
      id: 'contact',
      header: 'CONTACTO',
      render: (society) => (
        <>
          <div className="societies-view__soc-email">{society.email}</div>
          <div className="societies-view__soc-phone">{society.phone}</div>
        </>
      )
    },
    {
      id: 'warehouses',
      header: 'ALMACENES',
      align: 'center',
      render: (society) => {
        const count = linkedWarehouses(society.id).length;
        return (
          <span className={`societies-view__wh-badge ${count > 0 ? 'has-warehouses' : 'no-warehouses'}`}>
            {count}
          </span>
        );
      }
    }
  ];

  const renderCostCentersSkeleton = () => (
    <div className="societies-view__cc-list">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="societies-view__cc-item">
          <Skeleton />
          <Skeleton />
          <Skeleton />
        </div>
      ))}
    </div>
  );

  return (
    <>
      <MasterDetailLayout isOpen={Boolean(selectedSociety || isCreateOpen)} drawer>
        <div className="societies-view">
          <div className="societies-view__header-row">
            <ModuleHeader
              title="Gestión_De_Sociedades"
              subtitle="Administración de entidades jurídicas, datos de facturación e identificación fiscal."
            />
            <button
              onClick={handleCreateNewClick}
              className="societies-view__create-btn"
              data-keep-detail
            >
              <Plus size={14} />
              Alta_Sociedad
            </button>
          </div>

          <div className="societies-view__toolbar">
            <div className="societies-view__search-wrapper">
              <Search className="societies-view__search-icon" size={14} />
              <input
                type="text"
                placeholder="FILTRAR POR NOMBRE O CIF..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="societies-view__search-input"
              />
            </div>
          </div>

          <DataTable
            data={listUnavailable ? [] : filteredSocieties}
            columns={columns}
            title={<><Building2 size={14} className="societies-view__table-title-icon" /> Sociedades_Registradas</>}
            subtitle={isLoading || listUnavailable ? undefined : `TOTAL: ${filteredSocieties.length}`}
            isLoading={isLoading}
            getRowId={(society) => society.id}
            onRowClick={handleSelectSociety}
            selectedRowId={selectedSociety?.id}
            emptyText={listUnavailable
              ? 'API NO_DISPONIBLE — no se pudo conectar con /companies-list'
              : 'No se encontraron sociedades registradas'}
            actionsHeader="ACCIONES"
            actionsColumn={(society) => (
              <RowActionsMenu
                actions={[
                  viewAction(() => handleSelectSociety(society)),
                  editAction(() => handleEditClick(society)),
                  // Eliminar no tiene API todavía: se muestra deshabilitado.
                  deleteAction(() => handleDelete(society.id, society.name), {
                    disabled: true,
                    title: 'No disponible: falta API de eliminación de sociedades'
                  })
                ]}
              />
            )}
          />
        </div>

        {isCreateOpen ? (
          <SidebarForm
            drawer
            title={editingId ? 'Modificar_Sociedad' : 'Alta_De_Sociedad'}
            onClose={closePanel}
            submitLabel={isSavingSociety ? 'Guardando...' : editingId ? 'Guardar Cambios' : 'Crear Sociedad'}
            submitDisabled={isSavingSociety}
            onSubmit={handleSaveNewSociety}
          >
            {createError && (
              <div className="societies-view__cc-error">{createError}</div>
            )}
            <div className="sidebar-form-group">
              <label className="sidebar-form-label">Razón Social *</label>
              <input
                type="text"
                value={newSocietyForm.name}
                onChange={(e) => { const v = e.target.value; setNewSocietyForm(f => ({ ...f, name: v })); }}
                placeholder="Marcial Pons Ediciones S.A."
                className="sidebar-form-input"
              />
            </div>
            <div className="sidebar-form-group">
              <label className="sidebar-form-label">CIF / Identificación Fiscal *</label>
              <input
                type="text"
                value={newSocietyForm.cif}
                onChange={(e) => { const v = e.target.value.toUpperCase(); setNewSocietyForm(f => ({ ...f, cif: v })); }}
                placeholder="A82345678"
                className="sidebar-form-input"
              />
            </div>
            <div className="sidebar-form-group">
              <label className="sidebar-form-label">Dirección Fiscal</label>
              <input
                type="text"
                value={newSocietyForm.address}
                onChange={(e) => { const v = e.target.value; setNewSocietyForm(f => ({ ...f, address: v })); }}
                placeholder="Calle Mayor 12, Madrid"
                className="sidebar-form-input"
              />
            </div>
            <div className="sidebar-form-grid-2">
              <div className="sidebar-form-group">
                <label className="sidebar-form-label">Teléfono</label>
                <input
                  type="text"
                  value={newSocietyForm.phone}
                  onChange={(e) => { const v = e.target.value; setNewSocietyForm(f => ({ ...f, phone: v })); }}
                  placeholder="913043303"
                  className="sidebar-form-input"
                />
              </div>
              <div className="sidebar-form-group">
                <label className="sidebar-form-label">Email</label>
                <input
                  type="email"
                  value={newSocietyForm.email}
                  onChange={(e) => { const v = e.target.value; setNewSocietyForm(f => ({ ...f, email: v })); }}
                  placeholder="facturacion@pons.es"
                  className="sidebar-form-input"
                />
              </div>
            </div>
          </SidebarForm>
        ) : selectedSociety ? (
          <DetailPanel
            drawer
            title="Ficha_De_Sociedad"
            onClose={closePanel}
            headerActions={
              <>
                <DetailHeaderButton
                  icon={Layers}
                  label="Centros de coste"
                  onClick={() => openCostCenters(selectedSociety)}
                />
                <DetailHeaderButton
                  icon={Edit3}
                  label="Editar"
                  onClick={() => handleEditClick(selectedSociety)}
                />
              </>
            }
          >
            <DetailIdentity
              icon={Building2}
              reference={selectedSociety.id}
              name={selectedSociety.name}
            />

            <DetailFieldList>
              <DetailField icon={FileText} label="CIF_FACTURACIÓN">{selectedSociety.cif}</DetailField>
              <DetailField icon={MapPin} label="DIRECCIÓN_FISCAL">{selectedSociety.address || 'No declarada'}</DetailField>
              <DetailField icon={Phone} label="TELÉFONO">{selectedSociety.phone || 'No declarado'}</DetailField>
              <DetailField icon={Mail} label="CORREO_ELECTRÓNICO">
                <span className="detail-field-value lowercase">{selectedSociety.email || 'No declarado'}</span>
              </DetailField>
            </DetailFieldList>

            {aeatAccess !== 'none' && (
            <DetailSection icon={ShieldCheck} title="Certificado_AEAT (DIVA)">
              {(['test', 'prod'] as AeatEnvironment[]).map(env => {
                const item = selectedSociety.aeat && selectedSociety.aeat[env];
                if (!item) return null;
                return (
                  <div key={env} className="societies-view__aeat-summary">
                    <div>{env === 'prod' ? 'PRODUCCIÓN' : 'PRUEBAS'} · ILR {item.ilrCode}</div>
                    <div>{item.subject}</div>
                    <div>Caduca: {item.validTo.slice(0, 10)}</div>
                  </div>
                );
              })}
              {aeatError && <div className="societies-view__cc-error">{aeatError}</div>}
              {aeatAccess === 'full' ? (
              <div className="societies-view__aeat-form">
                <select
                  className="societies-view__cc-input"
                  value={aeatForm.environment}
                  onChange={(e) => { const v = e.target.value as AeatEnvironment; setAeatForm(f => ({ ...f, environment: v })); }}
                >
                  <option value="test">PRUEBAS</option>
                  <option value="prod">PRODUCCIÓN</option>
                </select>
                <input
                  type="text"
                  className="societies-view__cc-input"
                  placeholder="CÓDIGO ILR"
                  value={aeatForm.ilrCode}
                  onChange={(e) => { const v = e.target.value; setAeatForm(f => ({ ...f, ilrCode: v })); }}
                />
                <label className="societies-view__aeat-file">
                  <span>Certificado (.pem/.crt)</span>
                  <span className="societies-view__aeat-file-row">
                    <span className="societies-view__aeat-file-btn">
                      <Paperclip size={12} /> {aeatCertFile ? 'CAMBIAR' : 'SELECCIONAR'}
                    </span>
                    <span className={`societies-view__aeat-file-name${aeatCertFile ? ' is-set' : ''}`}>
                      {aeatCertFile ? aeatCertFile.name : 'Ningún archivo'}
                    </span>
                  </span>
                  <input
                    type="file"
                    accept=".pem,.crt,.cer"
                    className="societies-view__aeat-file-input"
                    onChange={(e) => { readTextFile(e.target.files?.[0], setAeatCertFile); e.target.value = ''; }}
                  />
                </label>
                <label className="societies-view__aeat-file">
                  <span>Clave privada (.pem/.key)</span>
                  <span className="societies-view__aeat-file-row">
                    <span className="societies-view__aeat-file-btn">
                      <Paperclip size={12} /> {aeatKeyFile ? 'CAMBIAR' : 'SELECCIONAR'}
                    </span>
                    <span className={`societies-view__aeat-file-name${aeatKeyFile ? ' is-set' : ''}`}>
                      {aeatKeyFile ? aeatKeyFile.name : 'Ningún archivo'}
                    </span>
                  </span>
                  <input
                    type="file"
                    accept=".pem,.key"
                    className="societies-view__aeat-file-input"
                    onChange={(e) => { readTextFile(e.target.files?.[0], setAeatKeyFile); e.target.value = ''; }}
                  />
                </label>
                <input
                  type="password"
                  className="societies-view__cc-input"
                  placeholder="CONTRASEÑA DE LA CLAVE (si la tiene)"
                  autoComplete="new-password"
                  value={aeatForm.passphrase}
                  onChange={(e) => { const v = e.target.value; setAeatForm(f => ({ ...f, passphrase: v })); }}
                />
                <button
                  type="button"
                  className="societies-view__cc-add-btn"
                  onClick={handleSaveAeat}
                  disabled={isSavingAeat}
                >
                  {isSavingAeat ? '...' : selectedSociety.aeat && selectedSociety.aeat[aeatForm.environment] ? 'Reemplazar certificado' : 'Guardar certificado'}
                </button>
              </div>
              ) : !selectedSociety.aeat ? (
                <div className="societies-view__wh-empty">Esta sociedad no tiene certificado AEAT.</div>
              ) : null}
            </DetailSection>
            )}

            <DetailSection
              icon={WarehouseIcon}
              title={`Almacenes_Asociados (${linkedWarehouses(selectedSociety.id).length})`}
            >
              {linkedWarehouses(selectedSociety.id).length === 0 ? (
                <div className="societies-view__wh-empty">
                  Ningún almacén ligado a esta sociedad
                </div>
              ) : (
                <div className="societies-view__wh-list">
                  {linkedWarehouses(selectedSociety.id).map(wh => (
                    <div key={wh.id} className="societies-view__wh-item">
                      <div>
                        <span className="societies-view__wh-item-name">{wh.name}</span>
                        <span className="societies-view__wh-item-type">
                          {wh.type === 'physical' ? 'FÍSICO' : 'VIRTUAL'}
                        </span>
                      </div>
                      <span className="societies-view__wh-item-capacity">
                        {Math.round((wh.used / wh.capacity) * 100)}% Cap.
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </DetailSection>
          </DetailPanel>
        ) : null}
      </MasterDetailLayout>

      <Modal
        isOpen={isCostCentersOpen}
        type="info"
        title={`Centros de coste — ${selectedSociety?.name || ''}`}
        message="Maestro analítico de la sociedad. El backend solo permite consultar y dar de alta: no hay edición ni borrado."
        showCancel={false}
        cancelText="Cerrar"
        onCancel={() => setIsCostCentersOpen(false)}
      >
        <div className="societies-view__cc-panel">
          {costCentersError && (
            <div className="societies-view__cc-error">{costCentersError}</div>
          )}

          {costCentersLoading ? (
            renderCostCentersSkeleton()
          ) : costCenters.length === 0 ? (
            <div className="societies-view__cc-empty">Ningún centro de coste dado de alta en esta sociedad</div>
          ) : (
            <div className="societies-view__cc-list">
              {costCenters.map(cc => (
                <div key={cc.id} className="societies-view__cc-item">
                  <span className="societies-view__cc-code">{cc.code}</span>
                  <span className="societies-view__cc-name">{cc.name}</span>
                  <span className={`module-badge ${cc.status}`}>{cc.status}</span>
                </div>
              ))}
            </div>
          )}

          <div className="societies-view__cc-form">
            <input
              type="text"
              value={newCostCenterCode}
              onChange={(e) => setNewCostCenterCode(e.target.value.toUpperCase())}
              placeholder="CÓDIGO"
              className="societies-view__cc-input code"
            />
            <input
              type="text"
              value={newCostCenterName}
              onChange={(e) => setNewCostCenterName(e.target.value)}
              placeholder="NOMBRE DEL CENTRO"
              className="societies-view__cc-input"
            />
            <button
              type="button"
              onClick={handleAddCostCenter}
              disabled={isSavingCostCenter}
              className="societies-view__cc-add-btn"
            >
              {isSavingCostCenter ? '...' : '+ Añadir'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={!!deleteModalSociety}
        type="urgent"
        title="Eliminar sociedad"
        message={`¿Está seguro de que desea eliminar la sociedad "${deleteModalSociety?.name}"? Esta acción es irreversible.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        onConfirm={confirmDeleteSociety}
        onCancel={() => setDeleteModalSociety(null)}
      />
    </>
  );
};
